import React, { createContext, useContext, useReducer, useEffect } from 'react';
import axios from 'axios';
import '../config/axios'; // Configure axios base URL

// Initial state
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true, // Start as true to prevent redirects during initial check
  error: null,
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  LOAD_USER_START: 'LOAD_USER_START',
  LOAD_USER_SUCCESS: 'LOAD_USER_SUCCESS',
  LOAD_USER_FAILURE: 'LOAD_USER_FAILURE',
  CLEAR_ERROR: 'CLEAR_ERROR',
  UPDATE_USER: 'UPDATE_USER',
};

// Reducer
function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
    case AUTH_ACTIONS.LOAD_USER_START:
      return {
        ...state,
        loading: true,
        error: null,
      };
      
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        error: null,
      };
      
    case AUTH_ACTIONS.LOAD_USER_SUCCESS:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        loading: false,
        error: null,
      };
      
    case AUTH_ACTIONS.LOGIN_FAILURE:
    case AUTH_ACTIONS.LOAD_USER_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: action.payload,
      };
      
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      };
      
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
      
    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };
      
    default:
      return state;
  }
}

// Create context
const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Set up axios defaults
  useEffect(() => {
    if (state.token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
      localStorage.setItem('token', state.token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
    }
  }, [state.token]);

  // Load user on app start if token exists
  useEffect(() => {
    const loadUser = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        // Set token in axios headers first
        axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;

        try {
          // Fetch actual user data from API
          const response = await axios.get('/api/auth/me');
          if (response.data.success && response.data.user) {
            dispatch({
              type: AUTH_ACTIONS.LOGIN_SUCCESS,
              payload: { user: response.data.user, token: savedToken },
            });
          } else {
            // Token invalid, clear it
            localStorage.removeItem('token');
            delete axios.defaults.headers.common['Authorization'];
            dispatch({ type: AUTH_ACTIONS.LOAD_USER_FAILURE, payload: null });
          }
        } catch (error) {
          console.error('Failed to load user:', error);
          // Token invalid or expired, clear it
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          dispatch({ type: AUTH_ACTIONS.LOAD_USER_FAILURE, payload: null });
        }
      } else {
        // No token found, set loading to false
        dispatch({ type: AUTH_ACTIONS.LOAD_USER_FAILURE, payload: null });
      }
    };

    loadUser();
  }, []);

  // Actions
  const login = async (email, password) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      // Demo credentials check
      if (email === 'demo@studiosivad.com' && password === 'demo123') {
        const user = {
          id: 1,
          name: 'Demo User',
          email: 'demo@studiosivad.com',
          role: 'admin',
          systemRole: 'admin',
          studioSivadRole: 'admin',
          brands: []
        };
        const token = 'demo-token-' + Date.now();

        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user, token },
        });

        return { success: true };
      }

      // If not demo credentials, try API
      const response = await axios.post('/api/auth/login', {
        email,
        password,
      });

      const { user, token } = response.data;

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, token },
      });

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Invalid credentials. Try demo@studiosivad.com / demo123';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });

      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    
    try {
      const response = await axios.post('/api/auth/register', userData);
      
      const { user, token } = response.data;
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, token },
      });
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Registration failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });
      
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  };

  const loadUser = async () => {
    dispatch({ type: AUTH_ACTIONS.LOAD_USER_START });
    
    try {
      const response = await axios.get('/api/auth/me');
      
      dispatch({
        type: AUTH_ACTIONS.LOAD_USER_SUCCESS,
        payload: response.data,
      });
    } catch (error) {
      dispatch({
        type: AUTH_ACTIONS.LOAD_USER_FAILURE,
        payload: error.response?.data?.error || 'Failed to load user',
      });
    }
  };

  const updateUser = (userData) => {
    dispatch({
      type: AUTH_ACTIONS.UPDATE_USER,
      payload: userData,
    });
  };

  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  const forgotPassword = async (email) => {
    try {
      await axios.post('/api/auth/forgot-password', { email });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to send reset email',
      };
    }
  };

  const resetPassword = async (token, password) => {
    try {
      await axios.post('/api/auth/reset-password', { token, password });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reset password',
      };
    }
  };

  // Helper functions
  const hasSystemRole = (role) => {
    return state.user?.systemRole === role;
  };

  const hasStudioSivadRole = (role) => {
    return state.user?.studioSivadRole === role;
  };

  const hasBrandAccess = (brandId) => {
    return state.user?.brands?.some(
      (brand) => brand.brandId === brandId
    );
  };

  const getBrandRole = (brandId) => {
    const brand = state.user?.brands?.find(
      (brand) => brand.brandId === brandId
    );
    return brand?.role || null;
  };

  const hasBrandPermission = (brandId, permission) => {
    const brand = state.user?.brands?.find(
      (brand) => brand.brandId === brandId
    );

    if (!brand) return false;
    if (brand.role === 'owner') return true;

    return brand.permissions.includes(permission);
  };

  // Onboarding helpers
  const getOnboardingStatus = () => {
    return state.user?.onboarding || {
      status: 'not_started',
      currentStep: 'add_brand',
      completedAt: null
    };
  };

  const hasBrandWithCompletePlan = () => {
    // Check if user has hasBrandWithCompletePlan flag from API
    if (state.user?.hasBrandWithCompletePlan !== undefined) {
      return state.user.hasBrandWithCompletePlan;
    }
    // Fallback to checking brandPlanStatuses
    return state.user?.brandPlanStatuses?.some(b => b.isComplete) || false;
  };

  const isOnboardingComplete = () => {
    const onboarding = getOnboardingStatus();
    // Onboarding is complete when:
    // 1. User has explicitly completed onboarding, OR
    // 2. User has at least one brand with a complete plan
    return onboarding.status === 'completed' || hasBrandWithCompletePlan();
  };

  const updateOnboarding = async (updates) => {
    try {
      const response = await axios.patch('/api/auth/onboarding', updates);
      if (response.data.success) {
        dispatch({
          type: AUTH_ACTIONS.UPDATE_USER,
          payload: { onboarding: response.data.onboarding }
        });
        return { success: true };
      }
      return { success: false, error: 'Failed to update onboarding' };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update onboarding status'
      };
    }
  };

  const getUserBrands = () => {
    return state.user?.brands || [];
  };

  const getBrandPlanStatuses = () => {
    return state.user?.brandPlanStatuses || [];
  };

  // Context value
  const value = {
    ...state,
    login,
    register,
    logout,
    loadUser,
    updateUser,
    clearError,
    forgotPassword,
    resetPassword,
    hasSystemRole,
    hasStudioSivadRole,
    hasBrandAccess,
    getBrandRole,
    hasBrandPermission,
    // Onboarding helpers
    getOnboardingStatus,
    hasBrandWithCompletePlan,
    isOnboardingComplete,
    updateOnboarding,
    getUserBrands,
    getBrandPlanStatuses,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};